import {
  Link,
  useNavigate
} from "react-router-dom";

import { useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';

import { Card, Typography, Box, TextField, IconButton, Popover } from '@mui/material';

import {
  faChartLine, faLeaf, faGraduationCap, faFutbol, faUsers,
  faFlaskVial, faGlobe, faMasksTheater, faHeartbeat, faGavel,
  faMicrochip,
  faWifi,
  faTrashCan,
  faPen,
  faCheck
} from '@fortawesome/free-solid-svg-icons';

import useStyles from '../../../../styles/global-mui.js';
import { deleteLive, updateLive } from '../../../../actions/lives.js';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DsasButton from "../../../../v1/components/DsasButton/DsasButton.js";
import { PERMISSIONS } from "../../../../permissions/permissionKeys.js";
import { Can } from "../../../../permissions/Can.js";
import { confirmActionNotification } from "../../../../actions/notifications.js";
import { useNotifications } from "../../../../v2/notifications/NotificationManager.js";
import LiveExport from "../../../Live/LiveExport.js";
import { GET_LIVE_DETAILS } from "../../../../constants/actionTypes.js";

const Live = ({ live, dayStats, setCurrentId, reload }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const classes = useStyles();

  const { addNotification } = useNotifications();

  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(live.name || '');
  const [isSavingName, setIsSavingName] = useState(false);

  const [dayStatsAnchorEl, setDayStatsAnchorEl] = useState(null);

  const sortedDays = (dayStats?.days || []).slice().sort((a, b) => a.date.localeCompare(b.date));
  const totalDays = dayStats?.totalDays || 0;
  const isDayStatsOpen = Boolean(dayStatsAnchorEl);

  useEffect(() => {
    setEditedName(live.name || '');
  }, [live.name]);

  const getDisciplineIcon = () => {
    switch (live.discipline) {
      case 'economie':
        return faChartLine;
      case 'environnement':
        return faLeaf;
      case 'education':
        return faGraduationCap;
      case 'sport':
        return faFutbol;
      case 'societe':
        return faUsers;
      case 'science':
        return faFlaskVial;
      case 'international':
        return faGlobe;
      case 'culture':
        return faMasksTheater;
      case 'sante':
        return faHeartbeat;
      case 'politique':
        return faGavel;
      case 'technologie':
        return faMicrochip;
      default:
        return faGlobe;
    }
  };

  const getCategoryClassName = () => {
    switch (live.discipline) {
      case 'economie':
        return 'economie-title';
      case 'environnement':
        return 'environnement-title';
      case 'education':
        return 'education-title';
      case 'sport':
        return 'sport-title';
      case 'societe':
        return 'societe-title';
      case 'science':
        return 'science-title';
      case 'international':
        return 'international-title';
      case 'culture':
        return 'culture-title';
      case 'sante':
        return 'sante-title';
      case 'politique':
        return 'politique-title';
      case 'technologie':
        return 'technologie-title';
      default:
        return 'other-title';
    }
  };

  const getBtnClassName = () => {
    switch (live.discipline) {
      case 'economie':
        return 'btn-blue-secondary';
      case 'environnement':
        return 'btn-green';
      case 'education':
        return 'btn-purple';
      case 'sport':
        return 'btn-blue';
      case 'societe':
        return 'btn-orange';
      case 'science':
        return 'btn-green-alt';
      case 'international':
        return 'btn-blue-alt';
      case 'culture':
        return 'btn-purple-alt';
      case 'sante':
        return 'btn-green-secondary';
      case 'politique':
        return 'btn-danger';
      case 'technologie':
        return 'btn-blue-logo';
      default:
        return 'btn-grey';
    }
  };

  const startEditName = () => {
    setEditedName(live.name || '');
    setIsEditingName(true);
  };

  const cancelEditName = () => {
    setEditedName(live.name || '');
    setIsEditingName(false);
  };

  const handleSaveName = () => {
    const trimmedName = editedName.trim();

    if (!trimmedName) {
      addNotification({
        title: "Nom invalide",
        type: "error",
        description: "Le nom du live ne peut pas être vide."
      });
      return;
    }

    if (trimmedName === live.name) {
      setIsEditingName(false);
      return;
    }

    setIsSavingName(true);

    dispatch(updateLive(
        live._id,
        { name: trimmedName },
        () => {
            setIsSavingName(false);
            setIsEditingName(false);
            reload?.();

            addNotification({
            title: "Modification",
            type: "success",
            description: `Le live "${trimmedName}" a bien été modifié.`
            });
        },
        () => {
            setIsSavingName(false);

            addNotification({
            title: "Erreur",
            type: "error",
            description: "Impossible de modifier le nom du live."
            });
        }
        ));
    };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveName();
    }
    if (e.key === 'Escape') {
      cancelEditName();
    }
  };

  return (
    <Card className={classes.customCardVariant}>
      {/* Card header */}
      <Box className='live-card padding-wrapper cardHeaderFlex'>
        <Box className={`live-title centered-wrapper ${getCategoryClassName()}`}>
          <Box><i>Direct</i></Box>
          <Box sx={{ paddingLeft: '5px' }}><b>{live.discipline}</b></Box>
          <Box sx={{ paddingLeft: '5px', paddingTop: 0 }}>
            <FontAwesomeIcon
              icon={faWifi}
              style={{ fontSize: 'medium', rotate: '90deg', height: '16px' }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FontAwesomeIcon
            icon={getDisciplineIcon()}
            className={`${getCategoryClassName()}`}
            style={{ float: 'right', height: '20px', paddingTop: '0px' }}
          />
        </Box>
      </Box>

      <Box className='large-separator'></Box>

      <Box
        className='large-padding-left large-padding-right padding-wrapper column-wrapper'
        sx={{ minHeight: '73px' }}
      >
        <Typography className='cardSubtitle'>
          {new Date(live.dateTime).toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })}
        </Typography>

        <Can permission={PERMISSIONS.DIVERS_LIVES_HOME_DAYSTATS_DISPLAY}>
        {totalDays > 0 && (
          <>
            <Typography
              variant="body2"
              sx={{
                width: 'fit-content',
                cursor: 'pointer',
                color: 'text.secondary',
                textDecoration: 'underline',
                mb: 0.5,
              }}
              onClick={(event) => setDayStatsAnchorEl(event.currentTarget)}
            >
              {totalDays} journée{totalDays > 1 ? 's' : ''}
            </Typography>

            <Popover
              open={isDayStatsOpen}
              anchorEl={dayStatsAnchorEl}
              onClose={() => setDayStatsAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
            >
              <Box sx={{ p: 2, minWidth: 220 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Journées du direct
                </Typography>

                {sortedDays.map((day) => (
                  <Box
                    key={day.date}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      py: 0.5,
                    }}
                  >
                    <Typography variant="body2">
                      {new Date(`${day.date}T12:00:00`).toLocaleDateString('fr-FR')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {day.count} post{day.count > 1 ? 's' : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Popover>
          </>
        )}
      </Can>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEditingName ? (
            <TextField
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onKeyDown={handleNameKeyDown}
              size="small"
              fullWidth
              autoFocus
              disabled={isSavingName}
              variant="outlined"
            />
          ) : (
            <Typography
              className='cardTitle'
              sx={{ cursor: 'default', flex: 1 }}
            >
              {live.name}
            </Typography>
          )}

          <Can permission={PERMISSIONS.DIVERS_LIVES_HOME_NAME_EDIT}>
            {isEditingName ? (
              <IconButton
                onClick={handleSaveName}
                disabled={isSavingName}
                size="small"
              >
                <FontAwesomeIcon icon={faCheck} />
              </IconButton>
            ) : (
              <IconButton
                onClick={startEditName}
                size="small"
              >
                <FontAwesomeIcon icon={faPen} />
              </IconButton>
            )}
          </Can>
        </Box>

        {/* {!isEditingName && (
          <Can permission={PERMISSIONS.DIVERS_LIVES_HOME_NAME_EDIT}>
            <Typography
              variant="caption"
              sx={{ cursor: 'pointer', width: 'fit-content' }}
              onClick={startEditName}
            >
              Modifier le nom
            </Typography>
          </Can>
        )} */}
      </Box>

      {/* Card content */}
      <Box sx={{ minHeight: '328px', display: 'inline-grid', alignContent: 'space-around' }}>
        {/* NEW INTERFACE */}
        <Box className='large-separator'></Box>
        <Box className='centered-column-wrapper padding-wrapper'>
          <Typography className='cardSubtitle' sx={{ marginBottom: '0', paddingBottom: '0', fontSize: 'small' }}>
            <i>Nouvelle interface</i>
          </Typography>
          <Link to={`/actualites/lives/${live._id}`} rel="noopener noreferrer">
            <DsasButton
              label={`Accéder au direct`}
              customColorClassname={getBtnClassName()}
              variant="flat"
            />
          </Link>
        </Box>

        <Box className='large-separator'></Box>

        {/* OLD INTERFACE */}
        <Box className='centered-column-wrapper padding-wrapper'>
          <Typography className='cardSubtitle' sx={{ marginBottom: '0', paddingBottom: '0', fontSize: 'small' }}>
            <i>Ancienne interface</i>
          </Typography>
          <DsasButton
            label={`Accéder au direct`}
            customColorClassname={getBtnClassName()}
            variant="flat"
            onClick={() => {
              dispatch({ type: GET_LIVE_DETAILS, payload: {} });
              navigate(`/divers/lives/${live._id}`);
            }}
          />
        </Box>
      </Box>

      <Box className='large-separator'></Box>

      {/* Boutons d'action */}
      <Box className='cardActions'>
        <Box className='centered-column-wrapper'>
          <Can permission={PERMISSIONS.DIVERS_LIVES_HOME_BUTTON_DELETE}>
            <DsasButton
              customCss={{ margin: '10px' }}
              label='Supprimer'
              icon={faTrashCan}
              onClick={() => {
                dispatch(confirmActionNotification(
                  "Êtes-vous sûr de vouloir supprimer le direct ?",
                  () => {
                    dispatch(deleteLive(live._id, () => {
                      reload();
                      addNotification({
                        title: "Suppression",
                        type: "success",
                        description: "Le live " + live.name + " a été supprimé."
                      });
                    }));
                  }
                ));
              }}
              variant='secondary'
              customClass='smaller'
            />
          </Can>
        </Box>

        <Box className='large-vertical-separator'></Box>

        <Box sx={{ display: 'grid', margin: 0 }}>
          <LiveExport idLive={live._id} isActu={true} live={live} />
        </Box>
      </Box>
    </Card>
  );
};

export default Live;